import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Center,
  Loader,
  Alert,
  useMantineColorScheme,
  Button,
  Group,
  ThemeIcon,
  Paper,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { IconAlertCircle, IconClock } from "@tabler/icons-react";
import { useMenuItems } from "../queries/menuQueries";
import { getMenuUrl } from "../utils/restaurant";
import { useRestaurantId } from "../utils/restaurant";
import pizzaVideo from "../assets/videos/pizza_optimized_v3.mp4";

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Get restaurant ID first (La Vida)
  const restaurantId = useRestaurantId();

  // Fetch menu items for the selected restaurant
  const {
    data: menuItems,
    isLoading,
    error,
  } = useMenuItems(true, restaurantId);

  // Extract unique categories from menu items
  const categories = Array.from(
    new Set(menuItems?.map((item) => item.category) || [])
  );

  const handleRestaurantClick = () => {
    navigate(getMenuUrl(restaurantId));
  };

  const handleCategoryClick = (category: string) => {
    const baseUrl = getMenuUrl(restaurantId);
    navigate(`${baseUrl}?category=${encodeURIComponent(category)}`);
  };

  return (
    <Container size="xl" pb="xl">
      <Stack gap="xl">
        {/* Marketing Banner - Sticky & Pinned */}
        <Paper
          shadow="md"
          radius="lg"
          withBorder
          p="sm"
          style={{
            position: "sticky",
            top: 74, // Header height (64) + 10px gap
            zIndex: 99,
            backgroundColor: isDark
              ? "rgba(36, 36, 36, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(5px)",
            cursor: "pointer",
          }}
          onClick={handleRestaurantClick}
        >
          <Group wrap="nowrap" justify="space-between">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon
                size="lg"
                radius="md"
                variant="gradient"
                gradient={{ from: "orange", to: "red" }}
              >
                <IconClock size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="sm" style={{ lineHeight: 1.2 }}>
                  Gratis Sodavand! 🥤
                </Text>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.2 }}>
                  Hvis du bestiller mellem 13:00 og 17:00
                </Text>
              </div>
            </Group>
          </Group>
        </Paper>

        {/* Featured Restaurant Card */}
        <Card
          shadow="md"
          radius="lg"
          withBorder
          padding={0}
          style={{ cursor: "pointer", overflow: "hidden" }}
          onClick={handleRestaurantClick}
        >
          <Card.Section>
            <video
              src={pizzaVideo}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: "100%",
                height: "auto",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Card.Section>

          <Card.Section
            style={{
              backgroundColor: isDark
                ? "var(--mantine-color-dark-6)"
                : "var(--mantine-color-neutral-10)",
              padding: "2rem",
            }}
          >
            <Stack align="center">
              <Title order={1} size="2rem" ta="center" c="black">
                🚧 Under opbygning 🚧
              </Title>
              <Text c="dimmed" size="sm" ta="center">
                Vi arbejder på siden – kom snart tilbage!
              </Text>
            </Stack>
          </Card.Section>
        </Card>

        {/* Categories Section */}
        <Stack gap="md">
          <Title order={2} c="black" size="1.75rem">
            {t("home.categoriesTitle")}
          </Title>

          {isLoading && (
            <Center h={200}>
              <Loader size="lg" />
            </Center>
          )}

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title={t("menu.error")}
            >
              {t("menu.errorDescription")}
            </Alert>
          )}

          {!isLoading && !error && categories.length > 0 && (
            <Carousel
              slideSize={{ base: "100%", sm: "50%", md: "33.333%" }}
              slideGap="md"
              controlsOffset="xs"
              emblaOptions={{
                loop: true,
              }}
            >
              {categories.map((category) => (
                <Carousel.Slide key={category}>
                  <Card
                    shadow="sm"
                    radius="lg"
                    withBorder
                    padding="xl"
                    style={{
                      cursor: "pointer",
                      height: "150px",
                      backgroundColor: isDark
                        ? "var(--mantine-color-dark-6)"
                        : "#f5f3e6",
                      borderColor: isDark
                        ? "var(--mantine-color-dark-4)"
                        : "var(--mantine-color-neutral-3)",
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <Stack align="center" justify="center" h="100%">
                      <Title
                        order={3}
                        fw={900}
                        // c={isDark ? "neutral.2" : "neutral.8"}
                        //  c="#da7518"
                        // c="#b46115"
                        // c="#894608"
                        c="#7f4713"
                        ta="center"
                      >
                        {category}
                      </Title>
                    </Stack>
                  </Card>
                </Carousel.Slide>
              ))}
            </Carousel>
          )}
        </Stack>

        {/* Game Section */}
        <Stack align="center" mt="xl">
          <Button
            variant="gradient"
            gradient={{ from: "orange", to: "red" }}
            size="lg"
            radius="xl"
            onClick={() => navigate("/game")}
            fullWidth
            style={{ maxWidth: 300 }}
          >
            Spil Flappy Smagly 🍕
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
